import React, { useState } from 'react';
import { useCalculator } from '../context/CalculatorContext';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { 
  ArrowLeft, 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  Save, 
  AlertTriangle, 
  ChevronLeft, 
  Settings, 
  Zap, 
  Activity, 
  Percent,
  Thermometer,
  Target,
  Award
} from 'lucide-react';
import { motion } from 'framer-motion';
import DynamicChartSystem from './charts/DynamicChartSystem';
import { shortenLargeNumber } from '../utils/formatters';
import MultimediaControls from './ui/MultimediaControls';

interface WhatIfComparisonViewProps {
  onBack: () => void;
}

const WhatIfComparisonView: React.FC<WhatIfComparisonViewProps> = ({ onBack }) => {
  const { 
    investment, 
    results, 
    whatIfScenario, 
    whatIfResults, 
    clearWhatIfScenario 
  } = useCalculator();
  
  const [activeChartCategory, setActiveChartCategory] = useState<'patrimony' | 'income' | 'cashflow' | 'comparison'>('patrimony');
  
  if (!results || !whatIfScenario) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
          </div>
          <p className="text-gray-600 font-medium">Calculando escenario...</p>
          <p className="text-gray-500 text-sm mt-2">Procesando datos del escenario "{whatIfScenario?.name || 'personalizado'}"</p>
          <button
            onClick={onBack}
            className="mt-6 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Volver a Configuración</span>
          </button>
        </div>
      </div>
    );
  }

  // Use whatIfResults if available, otherwise use regular results
  const scenarioResults = whatIfResults || results;

  // Prepare comparison data
  const prepareComparisonData = () => {
    const allBaseData = results.yearlyData.map(data => ({
      year: data.year,
      'Base - Patrimonio': data.citrusPatrimony,
      'Base - Ingreso': data.citrusIncome,
      'Base - Flujo': data.availableCashFlow
    }));

    const allScenarioData = scenarioResults.yearlyData.map(data => ({
      year: data.year,
      'Escenario - Patrimonio': data.citrusPatrimony,
      'Escenario - Ingreso': data.citrusIncome,
      'Escenario - Flujo': data.availableCashFlow
    }));

    // Combine both datasets
    const allCombinedData = allBaseData.map((base, index) => ({
      ...base,
      ...allScenarioData[index]
    }));

    return allCombinedData;
  };

  const comparisonData = prepareComparisonData();

  // Chart categories
  const chartCategories = [
    {
      id: 'patrimony',
      title: 'Comparación de Patrimonio',
      description: 'Evolución patrimonial: Base vs Escenario',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'from-green-500 to-green-600',
      availableTypes: ['line', 'bar', 'area'] as const
    },
    {
      id: 'income',
      title: 'Comparación de Ingresos',
      description: 'Ingresos anuales: Base vs Escenario',
      icon: <DollarSign className="w-5 h-5" />,
      color: 'from-blue-500 to-blue-600',
      availableTypes: ['line', 'bar', 'area'] as const
    },
    {
      id: 'cashflow',
      title: 'Comparación de Flujo de Efectivo',
      description: 'Flujo de efectivo: Base vs Escenario',
      icon: <Activity className="w-5 h-5" />,
      color: 'from-purple-500 to-purple-600',
      availableTypes: ['line', 'bar', 'area'] as const
    },
    {
      id: 'comparison',
      title: 'Resumen Comparativo',
      description: 'Comparación final de resultados',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'from-orange-500 to-orange-600',
      availableTypes: ['bar', 'pie'] as const
    }
  ];

  // Chart elements
  const chartElements = {
    patrimony: [
      {
        dataKey: 'Base - Patrimonio',
        name: 'Escenario Base',
        color: '#16a34a',
        strokeWidth: 3,
        fillOpacity: 0.6
      },
      {
        dataKey: 'Escenario - Patrimonio',
        name: whatIfScenario.name,
        color: '#dc2626',
        strokeWidth: 3,
        fillOpacity: 0.4,
        strokeDasharray: '5 5'
      }
    ],
    income: [
      {
        dataKey: 'Base - Ingreso',
        name: 'Escenario Base',
        color: '#16a34a',
        strokeWidth: 3,
        fillOpacity: 0.6
      },
      {
        dataKey: 'Escenario - Ingreso',
        name: whatIfScenario.name,
        color: '#dc2626',
        strokeWidth: 3,
        fillOpacity: 0.4,
        strokeDasharray: '5 5'
      }
    ],
    cashflow: [
      {
        dataKey: 'Base - Flujo',
        name: 'Escenario Base',
        color: '#16a34a',
        strokeWidth: 3,
        fillOpacity: 0.6
      },
      {
        dataKey: 'Escenario - Flujo',
        name: whatIfScenario.name,
        color: '#dc2626',
        strokeWidth: 3,
        fillOpacity: 0.4,
        strokeDasharray: '5 5'
      }
    ],
    comparison: [
      {
        dataKey: 'value',
        name: 'Patrimonio Final',
        color: '#16a34a',
        strokeWidth: 2,
        fillOpacity: 0.6
      }
    ]
  };

  // Prepare pie data for comparison
  const pieData = {
    comparison: [
      {
        name: 'Escenario Base',
        value: results.finalPatrimony,
        color: '#16a34a'
      },
      {
        name: whatIfScenario.name,
        value: scenarioResults.finalPatrimony,
        color: '#dc2626'
      }
    ]
  };

  // Summary data
  const summaryData = {
    patrimony: [
      {
        label: 'Base - Patrimonio Final',
        value: formatCurrency(results.finalPatrimony, investment.currencyFormat),
        color: 'text-green-600 bg-green-50'
      },
      {
        label: `${whatIfScenario.name} - Patrimonio`,
        value: formatCurrency(scenarioResults.finalPatrimony, investment.currencyFormat),
        color: 'text-red-600 bg-red-50'
      },
      {
        label: 'Diferencia',
        value: formatCurrency(scenarioResults.finalPatrimony - results.finalPatrimony, investment.currencyFormat),
        color: scenarioResults.finalPatrimony >= results.finalPatrimony ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'
      }
    ],
    income: [
      {
        label: 'Base - Ingreso Final',
        value: formatCurrency(results.finalMonthlyIncome * 12, investment.currencyFormat),
        color: 'text-green-600 bg-green-50'
      },
      {
        label: `${whatIfScenario.name} - Ingreso`,
        value: formatCurrency(scenarioResults.finalMonthlyIncome * 12, investment.currencyFormat),
        color: 'text-red-600 bg-red-50'
      },
      {
        label: 'Diferencia',
        value: formatCurrency((scenarioResults.finalMonthlyIncome - results.finalMonthlyIncome) * 12, investment.currencyFormat),
        color: scenarioResults.finalMonthlyIncome >= results.finalMonthlyIncome ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'
      }
    ],
    cashflow: [
      {
        label: 'Base - Flujo Final',
        value: formatCurrency(results.yearlyData[results.yearlyData.length - 1]?.availableCashFlow || 0, investment.currencyFormat),
        color: 'text-green-600 bg-green-50'
      },
      {
        label: `${whatIfScenario.name} - Flujo`,
        value: formatCurrency(scenarioResults.yearlyData[scenarioResults.yearlyData.length - 1]?.availableCashFlow || 0, investment.currencyFormat),
        color: 'text-red-600 bg-red-50'
      }
    ],
    comparison: [
      {
        label: 'Escenario Base',
        value: formatCurrency(results.finalPatrimony, investment.currencyFormat),
        color: 'text-green-600 bg-green-50'
      },
      {
        label: whatIfScenario.name,
        value: formatCurrency(scenarioResults.finalPatrimony, investment.currencyFormat),
        color: 'text-red-600 bg-red-50'
      }
    ]
  };

  // Calculate percentage differences
  const patrimonialDifference = ((scenarioResults.finalPatrimony - results.finalPatrimony) / results.finalPatrimony) * 100;
  const incomeDifference = ((scenarioResults.finalMonthlyIncome - results.finalMonthlyIncome) / results.finalMonthlyIncome) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-red-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-red-600 p-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <AlertTriangle className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Análisis de Escenario: {whatIfScenario.name}</h2>
                <p className="text-amber-100">{whatIfScenario.description}</p>
              </div>
            </div>
          </div>
          
          <div className="p-8 space-y-8">
            {/* Scenario Parameters */}
            <div className="bg-gradient-to-br from-amber-50 to-red-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-800 mb-4">Parámetros del Escenario</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-sm text-gray-600 mb-1">Utilidad Anual</div>
                  <div className="text-xl font-bold text-amber-700">
                    {whatIfScenario.averageProductionPerHectare && whatIfScenario.averageSalePricePerKg ? 
                      (((whatIfScenario.averageProductionPerHectare * whatIfScenario.averageSalePricePerKg * 0.1 * 0.65) / investment.certificateBasePrice) * 100).toFixed(1) :
                      ((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65) / investment.certificateBasePrice * 100).toFixed(1)
                    }%
                  </div>
                  <div className="text-xs text-gray-500">
                    Base: {((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65) / investment.certificateBasePrice * 100).toFixed(1)}%
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-sm text-gray-600 mb-1">Inflación</div>
                  <div className="text-xl font-bold text-red-700">
                    {whatIfScenario.inflationRate || investment.inflationRate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    Base: {investment.inflationRate}%
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-sm text-gray-600 mb-1">CETES</div>
                  <div className="text-xl font-bold text-blue-700">
                    {whatIfScenario.cetesRate || investment.cetesRate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    Base: {investment.cetesRate}%
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-sm text-gray-600 mb-1">Precio Limón</div>
                  <div className="text-xl font-bold text-yellow-700">
                    {whatIfScenario.lemonPriceIncrease || investment.lemonPriceIncrease}%
                  </div>
                  <div className="text-xs text-gray-500">
                    Base: {investment.lemonPriceIncrease}%
                  </div>
                </div>
              </div>
            </div>

            {/* Impact Analysis */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Análisis de Impacto</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className={`p-6 rounded-xl ${patrimonialDifference >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${patrimonialDifference >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                      <Target className={`w-5 h-5 ${patrimonialDifference >= 0 ? 'text-green-700' : 'text-red-700'}`} />
                    </div>
                    <h4 className={`font-medium ${patrimonialDifference >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                      Impacto Patrimonial
                    </h4>
                  </div>
                  <p className={`text-3xl font-bold mb-1 ${patrimonialDifference >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {patrimonialDifference >= 0 ? '+' : ''}{patrimonialDifference.toFixed(1)}%
                  </p>
                  <p className={`text-sm ${patrimonialDifference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    vs escenario base
                  </p>
                </div>
                
                <div className={`p-6 rounded-xl ${incomeDifference >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${incomeDifference >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                      <DollarSign className={`w-5 h-5 ${incomeDifference >= 0 ? 'text-green-700' : 'text-red-700'}`} />
                    </div>
                    <h4 className={`font-medium ${incomeDifference >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                      Impacto en Ingresos
                    </h4>
                  </div>
                  <p className={`text-3xl font-bold mb-1 ${incomeDifference >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {incomeDifference >= 0 ? '+' : ''}{incomeDifference.toFixed(1)}%
                  </p>
                  <p className={`text-sm ${incomeDifference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    vs escenario base
                  </p>
                </div>
                
                <div className="p-6 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Award className="w-5 h-5 text-blue-700" />
                    </div>
                    <h4 className="font-medium text-blue-800">Nivel de Resiliencia</h4>
                  </div>
                  <p className="text-3xl font-bold text-blue-700 mb-1">
                    {Math.abs(patrimonialDifference) < 10 ? 'Alta' : 
                     Math.abs(patrimonialDifference) < 25 ? 'Media' : 'Baja'}
                  </p>
                  <p className="text-sm text-blue-600">
                    resistencia al escenario
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Comparación Visual</h3>
              
              <DynamicChartSystem
                categories={chartCategories}
                chartData={{
                  patrimony: comparisonData,
                  income: comparisonData,
                  cashflow: comparisonData,
                  comparison: [
                    { investment: 'Escenario Base', value: results.finalPatrimony },
                    { investment: whatIfScenario.name, value: scenarioResults.finalPatrimony }
                  ]
                }}
                chartElements={chartElements}
                pieData={pieData}
                formatters={{
                  yLabel: (value) => shortenLargeNumber(value),
                  tooltip: (value) => formatCurrency(value, investment.currencyFormat)
                }}
                summaryData={summaryData}
                investment={investment}
                hideAdditionalOptions={true}
              />
            </div>

            {/* Recommendations */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Recomendaciones</h3>
              
              <div className="space-y-4">
                {patrimonialDifference >= 0 ? (
                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-green-800 mb-1">Escenario Favorable</h4>
                      <p className="text-sm text-green-700">
                        En este escenario, tu inversión en Cosecha Capital muestra un rendimiento superior al escenario base. 
                        Esto demuestra la resiliencia positiva de la inversión ante estas condiciones.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-red-800 mb-1">Escenario Desafiante</h4>
                      <p className="text-sm text-red-700">
                        En este escenario adverso, tu inversión muestra una disminución del {Math.abs(patrimonialDifference).toFixed(1)}% 
                        comparado con el escenario base. Sin embargo, Cosecha Capital mantiene su valor como activo real.
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-800 mb-1">Diversificación</h4>
                    <p className="text-sm text-blue-700">
                      Los resultados de este análisis refuerzan la importancia de la diversificación. 
                      Cosecha Capital ofrece una alternativa sólida con baja correlación a los mercados tradicionales.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Volver a Configuración</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  clearWhatIfScenario();
                  onBack();
                }}
                className="px-8 py-3 bg-gradient-to-r from-amber-500 to-red-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
              >
                <Settings className="w-5 h-5" />
                <span>Nuevo Escenario</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatIfComparisonView;